import { AlertTriangle } from "lucide-react";
import { READER_TRUST_CONFIDENCE } from "utils/groundTruth";

/**
 * How far each water-level sensor is from the labelled truth, under the water-level chart.
 *
 * Props: summary - summarizeOffsets() output; labelledCount - photos that count as truth in this run;
 * labels - labelSummary() output (by hand / by the ruler reader / waiting for review / refused);
 * agreement - readerAgreement() output or null; onReviewNext - opens the oldest photo waiting for review.
 * Offset = sensor - truth, so a positive number means the sensor reads high.
 */
const signed = (value) => `${value > 0 ? "+" : ""}${value.toFixed(2)}`;

export default function GroundTruthSummary({ summary = [], labelledCount = 0, labels = null, agreement = null, onReviewNext = null }) {
  return (
    <section
      className="col-span-1 rounded-2xl border border-slate-200 bg-white p-4 lg:col-span-2"
      data-testid="ground-truth-summary"
    >
      <h3 className="text-sm font-bold text-slate-800">Sensor error against the true level</h3>
      {labels && (
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600" data-testid="label-sources">
          <span>
            {labels.human} by hand · {labels.reader} by the ruler reader (confidence ≥ {READER_TRUST_CONFIDENCE})
            {labels.refused > 0 ? ` · ${labels.refused} it could not read` : ""}
          </span>
          {labels.review > 0 && (
            <span className="inline-flex items-center gap-1 font-semibold text-amber-800" data-testid="review-count">
              <AlertTriangle className="h-3.5 w-3.5" /> {labels.review} waiting for review
            </span>
          )}
          {labels.review > 0 && onReviewNext && (
            <button
              type="button"
              onClick={onReviewNext}
              className="rounded-lg border border-amber-300 bg-amber-50 px-2 py-0.5 font-bold text-amber-800"
            >
              Review next
            </button>
          )}
        </div>
      )}
      {agreement && (
        <p className="mt-1 text-xs text-slate-600" data-testid="reader-agreement">
          Ruler reader against your labels on {agreement.count} photo{agreement.count === 1 ? "" : "s"}: mean{" "}
          {agreement.meanMm > 0 ? "+" : ""}
          {agreement.meanMm} mm (reader minus you), largest {agreement.maxAbsMm} mm. A steady mean is a difference in how the
          float edge is read, not noise.
        </p>
      )}
      {labelledCount === 0 ? (
        <p className="mt-1 text-xs text-slate-500">
          No photos labelled yet. The ruler reader labels photos it reads with confidence ≥ {READER_TRUST_CONFIDENCE}; for
          the rest, open a photo from its marker on the water-level chart and enter the level you read off it. The red line
          on the chart and this comparison appear once there is at least one.
        </p>
      ) : summary.length === 0 ? (
        <p className="mt-1 text-xs text-slate-500">
          {labelledCount} photo{labelledCount === 1 ? "" : "s"} labelled, but no sensor reading within 5 minutes of any
          of them to compare against.
        </p>
      ) : (
        <>
          <p className="mt-1 text-xs text-slate-500">
            Sensor minus true level (field level, 30 cm ruler mark = 0), in cm, at the reading nearest each of the {labelledCount} labelled photo
            {labelledCount === 1 ? "" : "s"} (within 5 minutes). Positive means the sensor reads high. A small spread beside a large mean is
            an offset a single constant can remove; a large spread is not.
          </p>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-1 pr-4 font-semibold">Sensor</th>
                  <th className="py-1 pr-4 font-semibold">Compared</th>
                  <th className="py-1 pr-4 font-semibold">Mean</th>
                  <th className="py-1 pr-4 font-semibold">Median</th>
                  <th className="py-1 pr-4 font-semibold">Spread (SD)</th>
                  <th className="py-1 font-semibold">Largest</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((row) => (
                  <tr key={row.key} data-testid={`ground-truth-row-${row.key}`} className="border-t border-slate-100">
                    <td className="py-1 pr-4 font-semibold text-slate-700">{row.label}</td>
                    <td className="py-1 pr-4">{row.count}</td>
                    <td className="py-1 pr-4">{signed(row.mean)} cm</td>
                    <td className="py-1 pr-4">{signed(row.median)} cm</td>
                    <td className="py-1 pr-4">{row.sd === null || row.sd === undefined ? "—" : `${row.sd.toFixed(2)} cm`}</td>
                    <td className="py-1">{row.maxAbs.toFixed(2)} cm</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
